import { Check, Phone, PhoneHangUp, XClose } from "@untitledui/icons";
import {
  type ComponentPropsWithoutRef,
  type MouseEventHandler,
  type ReactNode,
  useId,
} from "react";
import { renderInlineMarkdown } from "../../utils/inlineMarkdown";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { StatusDot } from "../StatusDot";
import { avatarColorForSender, initialsFor } from "./avatarColor";
import styles from "./Notification.module.css";

/**
 * Notification — single notification card primitive.
 *
 * Matches the Figma "Notifications" component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1016:1446). The Figma file collapses
 * what the legacy codebase had as three separate feature components
 * (NotificationCard, CallNotificationBanner, ConversationCard) into
 * a single multi-variant shape.
 *
 * Four variants live behind a discriminated union on `variant`:
 *
 *   - **default**        — App push notification with header (icon +
 *                          app name + timestamp + close), title + body,
 *                          optional media slots, optional action pills.
 *                          This is the everyday case.
 *
 *   - **phoneCall**      — Live incoming call shape. Title (caller
 *                          name) + status subtitle (e.g. "Incoming
 *                          call…") on the left, two round IconButtons
 *                          on the right: green answer + red decline.
 *                          NO header, NO close, NO actions, NO media —
 *                          a call doesn't dismiss, you answer or
 *                          decline. Replaces the legacy
 *                          `CallNotificationBanner` feature component.
 *
 *   - **missedCall**     — Headered card with the body rendered in
 *                       `--text-negative` to signal a call-related
 *                       event the user isn't actively handling. Same
 *                       header as default (icon + app name + timestamp
 *                       + close); NO actions, NO media.
 *
 *                          Originally designed as a "missed call summary"
 *                          rendered after a `phoneCall` variant times out
 *                          without being answered, but consumers that
 *                          can't drive the `phoneCall` variant (e.g. the
 *                          Reflecto extension — it receives Android
 *                          `category: "call"` notifications but can't
 *                          answer or decline from the desktop) use this
 *                          variant as the surface for ALL call-related
 *                          cards: voicemails, incoming-call banners, and
 *                          missed summaries alike. The negative tone is
 *                          semantically neutral enough to cover the
 *                          "passive, can't-act-on-it" category. Consumers
 *                          in that broader use can override `role` (via
 *                          article-attr passthrough) to add live-region
 *                          semantics when a card surfaces a time-critical
 *                          event — see the `role` escape-hatch note
 *                          below.
 *
 *   - **conversation**   — Messaging app thread card. Same headered
 *                          shape as default (icon + app name + timestamp
 *                          + close + title + body preview), PLUS a
 *                          scrollable message thread list below the
 *                          body. Each message row shows a 28px Avatar
 *                          (computed color + initials) beside the
 *                          sender name and message text. An optional
 *                          `replySlot` at the bottom accepts a
 *                          `<ReplyComposer>` or any ReactNode.
 *                          NO actions, NO media, NO richMedia —
 *                          a conversation card has its own content
 *                          hierarchy. Replaces the legacy
 *                          `ConversationCard` feature component
 *                          (now deleted — consumers use
 *                          `ConversationNotificationItem`).
 *
 * **Why a discriminated union and not a flat type with optional
 * variant-specific props**: the three variants have genuinely
 * different required props. A `phoneCall` MUST have `onAnswer` and
 * `onDecline`, MUST NOT have `appName`/`timestamp`. A flat type
 * would let consumers pass invalid combinations at the type level
 * and crash at runtime. The discriminated union narrows correctly:
 * pass `variant: "phoneCall"` and TypeScript demands the call
 * handlers; omit `variant` (or pass `"default"`) and TypeScript
 * demands `appName` + `timestamp` + everything else.
 *
 * The Figma "Small media" and "Rich media" labels are NOT separate
 * variants — they're compositions of "Default + content in a slot",
 * so we treat them as orthogonal slot props on the Default shape:
 *   - `media`     → 48×48 thumbnail rendered beside the body
 *                   (Figma "Small media" composition)
 *   - `richMedia` → full-width 164px-tall slot rendered below the
 *                   body but above the action buttons (Figma "Rich
 *                   media" composition)
 * Both slots are optional and orthogonal — consumers can pass one,
 * the other, both, or neither. When both are passed, both render
 * in their respective positions; the design file doesn't anticipate
 * this combination but the component doesn't reject it.
 *
 * The component is **pure presentational**: it takes notification
 * data via props and never reads storage. Consumers (popup +
 * sidepanel notification lists) read storage and pass the parsed
 * notification down. This keeps the component testable, composable,
 * and barrel-safe (no transitive `webextension-polyfill` import).
 *
 * Layout (Default variant):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ [icon] AppName · 32m  •          [✕]                    │  ← header
 *   │                                                          │
 *   │ Lunch mode activated              [media]                │  ← body row
 *   │ Researchers are discovering how…  (48×48)                │
 *   │                                                          │
 *   │ ┌──────────────────────────────────────────────────┐    │
 *   │ │              richMedia (164px tall)              │    │
 *   │ └──────────────────────────────────────────────────┘    │
 *   │                                                          │
 *   │ [Reply] [Archive] [Mark as read]                        │  ← actions
 *   │                                                          │
 *   │ ┌────────────────────────────────────────────┬───────┐  │
 *   │ │ replySlot — consumer-supplied              │ Send  │  │
 *   │ └────────────────────────────────────────────┴───────┘  │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Layout (Phone call variant):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Mummy                                  [☎ ]   [☎ ]      │
 *   │ Incoming call…                         green   red       │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Layout (Missed call variant):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ [icon] Phone · 32m  •                       [✕]         │  ← header
 *   │ Mummy                                                    │  ← title
 *   │ Missed call    ← rendered in --text-negative             │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Layout (Conversation variant):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ [icon] Whatsapp · 32m  •                    [✕]         │  ← header
 *   │ Mummy                                                    │  ← title
 *   │ Coming tomorrow?                                         │  ← body preview
 *   │                                                          │
 *   │ [AV] Mummy                                               │  ← message row
 *   │      Hi. How are you?                                    │
 *   │ [AV] You                                                 │
 *   │      Hi. All good 😊                                     │
 *   │ [AV] Mummy                                               │
 *   │      Coming tomorrow?                                    │
 *   │                                                          │
 *   │ [reply composer — full-width]                            │  ← replySlot
 *   └──────────────────────────────────────────────────────────┘
 *
 * Action buttons: each action is a `{ label, onClick, state? }` and
 * renders as a Button (xs/secondary pill). Actions default to
 * fire-and-forget; consumers can opt individual actions into
 * per-action feedback by setting `state` to "sending" / "sent" /
 * "error" — see the `NotificationAction.state` JSDoc for the Button
 * prop mapping. Reply is a separate concept exposed via `replySlot`.
 *
 * Reply slot: the `replySlot?` prop on the Default variant accepts
 * arbitrary ReactNode and renders it below the actions row. Consumers
 * drop a controlled reply input (the Input primitive, a textarea, a
 * command palette, an emoji picker, etc.) into the slot without the
 * primitive having to know anything about reply state, focus
 * management, sent/error feedback, or the FCM call. See the JSDoc
 * on `DefaultNotificationProps.replySlot` for the full rationale.
 *
 * Status (unread): when `unread` is true, a StatusDot is rendered in
 * the header row to the right of the timestamp. Available on default
 * and missedCall variants.
 *
 * Close button: rendered ONLY when `onClose` is provided. The
 * Figma `hover` boolean (which controls visibility in the design
 * file) maps to the presence of the handler at runtime — if a
 * consumer doesn't wire dismissal, the X button doesn't render.
 * Available on default and missedCall variants. Phone call has no
 * close — you answer or decline.
 *
 * Accessibility:
 *   - The card is wrapped in `<article aria-labelledby={titleId}>`
 *     so the article landmark has an accessible name from its title.
 *   - The "·" separator between app name and timestamp is
 *     `aria-hidden` because it's purely decorative — it would
 *     announce as "middle dot" otherwise.
 *   - The app icon is `alt=""` (decorative) — the app name text is
 *     adjacent and announces the source.
 *   - The unread `StatusDot` carries an `aria-label` (from the
 *     `unreadLabel` prop, defaulting to `"Unread"`) so the
 *     unread state is announced — the dot is the ONLY indicator
 *     of unread (no border treatment, no adjacent text), so per
 *     StatusDot's own a11y contract it must be labeled.
 *     Consumers can override `unreadLabel` for i18n.
 *   - Phone call answer/decline IconButtons carry their own
 *     aria-labels (from `answerLabel` / `declineLabel`, defaulting
 *     to `"Answer call"` / `"Decline call"`). Consumers override
 *     for i18n.
 *   - Media slots accept arbitrary ReactNode — the consumer is
 *     responsible for the accessibility of whatever they put
 *     inside (alt text on images, etc.).
 *
 * Props passthrough: every variant extends
 * `ComponentPropsWithoutRef<"article">` so consumers can pass
 * `id`, `data-*`, arbitrary `aria-*`, etc. through to the
 * underlying article element. Crucial for the upcoming consumer
 * migration from the legacy `NotificationCard` — the side panel's
 * `IntersectionObserver` auto-read-marking flow queries by
 * `[data-msg-id]`, so consumers must be able to pass that
 * attribute through. `className`, `children`, and `aria-labelledby`
 * are in the Omit list because they're managed by this component.
 * Spread order: `{...rest}` BEFORE managed attrs so managed attrs
 * always win even if the Omit ever leaks.
 */

/** Variant enum. */
export type NotificationVariant = "default" | "phoneCall" | "missedCall" | "conversation";

/** A single action button rendered inline.
 *
 * By default actions are fire-and-forget — consumers click, the
 * `onClick` handler dispatches to whatever side-channel the app uses
 * (e.g. REPLY_NOTIFICATION / ACTION_NOTIFICATION messages to a
 * background worker in the Reflecto extension), and the button
 * returns to idle.
 *
 * For cases where the consumer wants inline feedback on the dispatch
 * result — "was this action received? did it succeed?" — the `state`
 * field drives a Button loading / disabled / icon swap via the
 * primitive-owned mapping below. The primitive stays state-free:
 * the consumer owns transitions (set "sending" on click, update to
 * "sent"/"error" when the dispatch resolves, reset to undefined
 * after a delay). */
export interface NotificationAction {
  /** Stable identifier used as the React `key` for this action's
   *  rendered Button. Optional — when omitted, the array index is
   *  used as the key. Pass an `id` (e.g. an Android action index
   *  or a server-supplied UUID) when actions can be reordered or
   *  when two actions might share the same `label` (multi-account
   *  "Reply" buttons, locale collisions). */
  id?: string;
  /** Visible button label. */
  label: string;
  /** Click handler. Receives the React mouse event so consumers can
   *  call `e.stopPropagation()` if the article ever grows its own
   *  click handler. */
  onClick: MouseEventHandler<HTMLButtonElement>;
  /** Optional per-action feedback state. Primitive mapping:
   *    - `"sending"` → Button `loading` (spinner replaces leading slot,
   *       button is disabled, label stays visible)
   *    - `"sent"`    → leading Check icon + disabled (feedback without
   *       re-dispatch risk)
   *    - `"error"`   → leading XClose icon + `variant="negative"` +
   *       disabled (red surfaces the failure; label still visible)
   *    - `undefined` → default secondary button (idle)
   *
   *  Consumers manage the state machine themselves. Typical pattern:
   *  set `"sending"` synchronously in the click handler, await the
   *  dispatch, set `"sent"` / `"error"` on resolution, and clear the
   *  state (set to `undefined`) after 2–3 seconds via setTimeout. The
   *  primitive does not auto-clear or enforce any timing. */
  state?: "sending" | "sent" | "error";
}

/** Props common to all variants. */
type CommonNotificationProps = {
  /** Title text (usually the message subject or sender name). */
  title: string;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"article">, "className" | "children" | "aria-labelledby">;

/** Shared shape for the headered variants (Default + MissedCall). They
 *  share the entire header (icon + name + timestamp + close), the
 *  title/body block, and the unread + close props. Extracted to a
 *  base type so the seven shared fields don't have to be duplicated
 *  on each variant — a future field rename to `appName` etc. only
 *  needs to happen in one place. */
type HeaderedNotificationBaseProps = CommonNotificationProps & {
  /** App name shown in the header (e.g. "Whatsapp"). */
  appName: string;
  /** App icon URL (base64 data URL or remote URL). Optional — when
   *  omitted, the icon slot collapses. */
  appIconUrl?: string;
  /** Pre-formatted relative timestamp (e.g. "32m", "yesterday"). */
  timestamp: string;
  /** Notification body / description. Default variant truncates to
   *  two lines with an ellipsis when it overflows; MissedCall
   *  variant renders this in --text-negative as the missed status. */
  body?: string;
  /** When true, `body` is parsed as inline markdown (bold/italic/code/
   *  links) and rendered as formatted React nodes instead of plain text.
   *  Set by the public-API path when the sender passed `markdown: true`.
   *  Inline-only — no block elements. See `renderInlineMarkdown`. */
  bodyMarkdown?: boolean;
  /** When true, renders a StatusDot in the header row. Replaces the
   *  legacy "unread border" affordance. */
  unread?: boolean;
  /** Accessible label for the unread StatusDot. Defaults to `"Unread"`. */
  unreadLabel?: string;
  /** Called when the user clicks the close (X) button. When omitted,
   *  the X button is NOT rendered. */
  onClose?: MouseEventHandler<HTMLButtonElement>;
  /** Accessible label for the close (X) button. Defaults to
   *  `"Dismiss notification"`. Consumers should pass a localized
   *  string from their i18n layer — Notification stays storage-free
   *  / i18n-free by design (same pattern as `unreadLabel`,
   *  `answerLabel`, `declineLabel`). */
  closeLabel?: string;
};

/** Default variant: standard app push notification.
 *
 * IMPORTANT: any new Default-only field added below MUST also be
 * destructured out of `props` in the shared Default+MissedCall render
 * branch (see the `as DefaultNotificationProps` cast block near the
 * bottom of this file). A field that's added here but forgotten there
 * will silently land in the `...rest` spread onto the `<article>`
 * DOM element and trigger React's "unknown prop" warning — the cast
 * means TypeScript can't catch this for you. The DOM-leak regression
 * test in `primitives.test.tsx` catches it at the attribute level,
 * but the cheaper fix is to remember to extend the destructure list
 * when you add a field. */
export type DefaultNotificationProps = HeaderedNotificationBaseProps & {
  variant?: "default";
  /** Optional inline 48×48 media thumbnail rendered beside the body
   *  (Figma "Small media" composition). */
  media?: ReactNode;
  /** Optional full-width 164px-tall media block rendered below the
   *  body and above the actions row (Figma "Rich media" composition). */
  richMedia?: ReactNode;
  /** Fire-and-forget action buttons rendered as a row of secondary
   *  pills below the body. */
  actions?: NotificationAction[];
  /** Optional reply UI slot rendered BELOW the actions row. The slot
   *  accepts arbitrary ReactNode — consumers typically pass the
   *  shared `Input` primitive + a send button, or a textarea + send
   *  button if multi-line is needed, or any other composite reply
   *  affordance. The primitive does NOT manage input state, focus,
   *  send-call wiring, or sent/error feedback — those all live in
   *  the consumer.
   *
   *  Why a slot, not a typed `replyValue` / `onReply` prop pair:
   *  Reply UX is consumer-specific (single-line Input vs. multi-line
   *  textarea vs. command palette vs. emoji picker vs. editor with
   *  @-mentions), and baking any of those into the primitive would
   *  either constrain the consumer or balloon the primitive's prop
   *  surface. The slot pattern keeps Notification storage-free /
   *  logic-free and lets each consumer ship its own reply
   *  implementation.
   *
   *  Layout: rendered as the last child of the article, in a
   *  dedicated `.replySlot` block with vertical spacing matching the
   *  actions row. Container has no padding/border/background of its
   *  own — visual chrome is the consumer's call. */
  replySlot?: ReactNode;
};

/** Phone call variant: live incoming call with answer/decline buttons. */
export type PhoneCallNotificationProps = CommonNotificationProps & {
  variant: "phoneCall";
  /** Status subtitle, e.g. "Incoming call…". Rendered in
   *  --text-success (green) to convey the live state. */
  subtitle?: string;
  /** Called when the user taps the green answer button. */
  onAnswer: MouseEventHandler<HTMLButtonElement>;
  /** Called when the user taps the red decline button. */
  onDecline: MouseEventHandler<HTMLButtonElement>;
  /** Accessible label for the answer button. Defaults to `"Answer call"`.
   *  Consumers should pass a localized string from their i18n layer. */
  answerLabel?: string;
  /** Accessible label for the decline button. Defaults to `"Decline call"`. */
  declineLabel?: string;
};

/** MissedCall variant: call-related card in negative tone. See the
 *  file-level JSDoc for the broader consumer use (any call-category
 *  notification a consumer can't drive through `phoneCall`) and the
 *  `role` article-attr override for live-region overlays. */
export type MissedCallNotificationProps = HeaderedNotificationBaseProps & {
  variant: "missedCall";
  // No additional props — MissedCall is the headered base shape with
  // the body rendered in --text-negative via CSS. The variant
  // explicitly does NOT accept `media` / `richMedia` / `actions` — if
  // a consumer needs media or actions on a missed call they want the
  // Default variant.
};

/** A single message in a conversation thread.
 *
 *  Consumers pass the full message history from storage; the Notification
 *  primitive renders each item as an Avatar + sender name + text row. */
export interface ConversationMessage {
  /** Stable React key — use the message id from storage. */
  id: string;
  /** Sender display name. `null` = device owner (rendered as
   *  `deviceOwnerLabel`, defaulting to `"You"`). */
  sender: string | null;
  /** Message text. */
  text: string;
  /** Epoch ms — accepted for future use (not rendered per-message in the
   *  current design; the notification-level header timestamp covers
   *  "when did this arrive"). */
  timestamp: number;
}

/** Conversation variant: messaging-app thread card.
 *
 *  IMPORTANT: same "destructure all variant-only fields out of props" rule
 *  as DefaultNotificationProps — any new field added here MUST also appear
 *  in the destructure block in the shared Default+Missed+Conversation render
 *  branch. Fields omitted from the destructure will leak into `...rest` and
 *  trigger React's "unknown prop" warning on the `<article>` DOM element. */
export type ConversationNotificationProps = HeaderedNotificationBaseProps & {
  variant: "conversation";
  /** Full message history. Per the team's design decision, the latest
   *  message appears BOTH in the `body` prop (as preview/headline) AND at
   *  the end of this list (for full-thread context). No de-duplication in
   *  the primitive — consumer passes both. */
  messages: ConversationMessage[];
  /** Label for the device owner (when `sender === null`). Default: "You".
   *  Consumers pass a localized string from their i18n layer. */
  deviceOwnerLabel?: string;
  /** Accessible label for the message list (`<ul>`). Defaults to
   *  `"Messages in conversation"`. Consumers should pass a localized
   *  string from their i18n layer — matches the pattern used by
   *  `unreadLabel`, `closeLabel`, `deviceOwnerLabel`. */
  messagesListLabel?: string;
  /** Reply UI slot — same pattern as `DefaultNotificationProps.replySlot`.
   *  Consumer composes `<ReplyComposer ... />` and passes it in.
   *  Rendered as the last child of the article, at the bottom of the card.
   *  When omitted, the slot collapses entirely (no wrapper div). */
  replySlot?: ReactNode;
};

/** Discriminated union over the four variants. */
export type NotificationProps =
  | DefaultNotificationProps
  | PhoneCallNotificationProps
  | MissedCallNotificationProps
  | ConversationNotificationProps;

/**
 * Internal "wide" shape for the shared Default+Missed+Conversation render
 * branch. Used exclusively as the cast target for destructuring every
 * known prop in one place so none leak into `...rest` and trip React's
 * "unknown prop" warnings on the underlying `<article>`. NOT part of the
 * public API — see the cast site in the render body for the rationale.
 *
 * A union intersection would narrow to `never` because the discriminant
 * values conflict; the wide optional shape sidesteps that. At runtime,
 * fields not present on the actual variant are `undefined` — the
 * conditional renders below skip them.
 */
type AllHeaderedProps = HeaderedNotificationBaseProps & {
  bodyMarkdown?: boolean;
  media?: ReactNode;
  richMedia?: ReactNode;
  actions?: NotificationAction[];
  replySlot?: ReactNode;
  messages?: ConversationMessage[];
  deviceOwnerLabel?: string;
  messagesListLabel?: string;
  variant?: string;
};

export function Notification(props: NotificationProps) {
  // Stable id for the article's accessible-name association.
  const titleId = useId();

  // ─── Phone call branch ────────────────────────────────────────
  // Narrow on `props.variant` directly so TypeScript can refine the
  // discriminated union without a cast. Using a derived `const variant`
  // would defeat the purpose of the union — the cast would compile but
  // a future refactor could silently drift.
  if (props.variant === "phoneCall") {
    const {
      title,
      subtitle,
      onAnswer,
      onDecline,
      answerLabel = "Answer call",
      declineLabel = "Decline call",
      // Strip ours so the rest spread doesn't include them.
      variant: _v,
      className,
      ...rest
    } = props;
    const callBodyClasses = styles.callBody;

    // Class list ordered to match every sibling primitive (Button,
    // IconButton, Input, Surface, SearchBox): component class first,
    // variant modifier second, consumer-supplied className LAST.
    const classes = [styles.notification, styles["variant-phoneCall"], className]
      .filter(Boolean)
      .join(" ");

    return (
      // role="alert" is the phoneCall variant's default: assistive tech
      // interrupts and announces an incoming call immediately on
      // insertion. Without this, AT wouldn't announce the call until
      // the user navigates into the article — by which time the call
      // may have rung out. The live region is non-negotiable for an
      // actively-ringing call.
      //
      // The missedCall + default variants do NOT set role by default
      // (neither is a live event in the primitive's canonical use).
      // Consumers can still override via the article-attr `...rest`
      // passthrough — e.g. a consumer that funnels all call-category
      // notifications through `missedCall` (because it can't drive
      // `phoneCall`) can pass `role="alert"` to regain the live-region
      // behavior for incoming-call surfaces. This is a supported
      // escape hatch; see the MissedCall variant JSDoc above.
      <article {...rest} role="alert" className={classes} aria-labelledby={titleId}>
        <div className={callBodyClasses}>
          <h3 id={titleId} className={styles.title}>
            {title}
          </h3>
          {subtitle && <p className={styles.callSubtitle}>{subtitle}</p>}
        </div>
        <div className={styles.callActions}>
          <IconButton
            icon={Phone}
            label={answerLabel}
            variant="default"
            size="m"
            onClick={onAnswer}
            className={styles.answerButton}
          />
          <IconButton
            icon={PhoneHangUp}
            label={declineLabel}
            variant="default"
            size="m"
            onClick={onDecline}
            className={styles.declineButton}
          />
        </div>
      </article>
    );
  }

  // ─── Default + Missed call + Conversation branch ─────────────
  // From here, props is DefaultNotificationProps | MissedCallNotificationProps
  // | ConversationNotificationProps. All three share the headered shape
  // (header + body + close); variants differ only by the .variant-{name}
  // CSS class and by which additional slots they expose.
  const variant = props.variant ?? "default";

  // Destructure ALL known props so the rest spread doesn't leak any of
  // them to the underlying <article> DOM element. Covers Default-only
  // fields (media / richMedia / actions / replySlot) AND Conversation-only
  // fields (messages / deviceOwnerLabel / messagesListLabel). React's
  // "unknown prop" warnings only surface in a real browser; the node test
  // environment (renderToStaticMarkup) doesn't catch them — the
  // DOM-leak regression tests below use explicit attribute assertions.
  //
  // `AllHeaderedProps` is declared at module scope above (see its JSDoc
  // for the union-narrowing rationale).
  const {
    title,
    appName,
    appIconUrl,
    timestamp,
    body,
    bodyMarkdown,
    unread,
    unreadLabel = "Unread",
    closeLabel = "Dismiss notification",
    onClose,
    media,
    richMedia,
    actions,
    replySlot,
    messages,
    deviceOwnerLabel = "You",
    messagesListLabel = "Messages in conversation",
    variant: _v,
    className,
    ...rest
  } = props as AllHeaderedProps;
  const bodyClasses = styles.body;
  const messageListClasses = styles.messageList;

  // Class list ordering matches the phoneCall branch + every sibling
  // primitive: component class first, variant modifier second, consumer
  // className LAST.
  const classes = [styles.notification, styles[`variant-${variant}`], className]
    .filter(Boolean)
    .join(" ");

  return (
    <article {...rest} className={classes} aria-labelledby={titleId}>
      <div className={styles.headerRow}>
        <div className={styles.headerMeta}>
          {appIconUrl && <img src={appIconUrl} alt="" className={styles.appIcon} />}
          <span className={styles.appName}>{appName}</span>
          <span className={styles.separator} aria-hidden="true">
            ·
          </span>
          <span className={styles.timestamp}>{timestamp}</span>
          {unread && <StatusDot label={unreadLabel} />}
        </div>
        {onClose && (
          <IconButton
            icon={XClose}
            label={closeLabel}
            variant="default"
            size="s"
            onClick={onClose}
          />
        )}
      </div>
      <div className={styles.bodyRow}>
        <div className={bodyClasses}>
          <h3 id={titleId} className={styles.title}>
            {title}
          </h3>
          {body && (
            <p
              className={[styles.bodyText, body.includes("\n") ? styles.bodyTextMultiline : null]
                .filter(Boolean)
                .join(" ")}
            >
              {bodyMarkdown ? renderInlineMarkdown(body) : body}
            </p>
          )}
        </div>
        {media && <div className={styles.mediaSlot}>{media}</div>}
      </div>
      {richMedia && <div className={styles.richMediaSlot}>{richMedia}</div>}
      {variant === "conversation" && messages && messages.length > 0 && (
        <ul className={messageListClasses} aria-label={messagesListLabel}>
          {messages.map((message) => {
            const senderName = message.sender ?? deviceOwnerLabel;
            return (
              <li key={message.id} className={styles.messageRow}>
                <Avatar
                  initials={initialsFor(senderName)}
                  color={avatarColorForSender(senderName)}
                />
                <div className={styles.messageContent}>
                  <span className={styles.senderName}>{senderName}</span>
                  <span className={styles.messageText}>{message.text}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {variant !== "conversation" && actions && actions.length > 0 && (
        <div className={styles.actions}>
          {actions.map((action, index) => {
            // Map NotificationAction.state → Button props; undefined
            // is idle (default secondary Button, enabled).
            const leadingIcon =
              action.state === "sent" ? Check : action.state === "error" ? XClose : undefined;
            return (
              <Button
                key={action.id ?? index}
                variant={action.state === "error" ? "negative" : "secondary"}
                size="xs"
                onClick={action.onClick}
                loading={action.state === "sending"}
                leadingIcon={leadingIcon}
                disabled={action.state !== undefined}
              >
                {action.label}
              </Button>
            );
          })}
        </div>
      )}
      {replySlot && <div className={styles.replySlot}>{replySlot}</div>}
    </article>
  );
}
